import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import React from "react";
import { ReactComponent as AlertTriangle } from "../../assets/alert-triangle.svg";
import { error } from "../../theme/themePrimitives";

interface ConfirmModalProps {
  openConfirmModal: boolean;
  setOpenConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  modalTitle: string;
  modalSubtitle: React.ReactNode;
  confirmButtonClick: () => void;
  handleModalConfirm?: () => void;
  confirmButtonText?: string;
  cancelButtonText?: string;
  modalLoading?: boolean;
  icon?: React.ReactNode;
  /**
   * Short warning line rendered under the title with a red triangle icon
   * (e.g. "Are you sure you want to proceed?"), followed by a divider
   * before modalSubtitle. Omit for the plain single-paragraph layout.
   */
  warningText?: string;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  openConfirmModal,
  setOpenConfirmModal,
  modalSubtitle,
  modalTitle,
  confirmButtonClick,
  handleModalConfirm,
  modalLoading,
  confirmButtonText = "Yes, I'm sure",
  cancelButtonText = "No, I'm not",
  icon,
  warningText,
}) => {
  const hasIcon = !!icon;
  const textAlign = hasIcon ? "center" : "left";
  const buttonJustify = hasIcon ? "center" : "flex-end";

  const handleConfirm = async () => {
    if (handleModalConfirm) {
      await handleModalConfirm();
    }
    confirmButtonClick();
  };

  return (
    <Modal open={openConfirmModal} onClose={() => setOpenConfirmModal(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "50%",
          maxWidth: "550px",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          alignItems: hasIcon ? "center" : "stretch",
        }}
      >
        {!hasIcon && (
          <IconButton
            sx={{
              position: "absolute",
              top: "16px",
              right: "16px",
              border: "none",
            }}
            onClick={() => setOpenConfirmModal(false)}
          >
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        )}

        {hasIcon && <Box sx={{ mb: 2 }}>{icon}</Box>}

        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ textAlign, mb: warningText ? 0.5 : 1, ...(!hasIcon && { pr: 5 }) }}
        >
          {modalTitle}
        </Typography>

        {warningText && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: hasIcon ? "center" : "flex-start",
                gap: 1,
                width: "100%",
              }}
            >
              <AlertTriangle
                style={{ width: 20, height: 20, color: error[600], flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: error[600] }}
              >
                {warningText}
              </Typography>
            </Box>
            <Divider sx={{ width: "100%", my: 2 }} />
          </>
        )}

        <Typography
          component="div"
          variant="body2"
          sx={{ textAlign, pb: "2rem" }}
        >
          {modalSubtitle}
        </Typography>

        <Box
          sx={{ display: "flex", justifyContent: buttonJustify, gap: "1rem" }}
        >
          <Button
            onClick={() => setOpenConfirmModal(false)}
            sx={{
              px: 2,
              border: "1px solid #D0D5DD",
              borderRadius: "8px",
              backgroundColor: "white !important",
              color: "black",
              "&:hover": {
                backgroundColor: "whitesmoke !important",
              },
            }}
          >
            {cancelButtonText}
          </Button>

          {modalLoading ? (
            <CircularProgress />
          ) : (
            <Button
              onClick={handleConfirm}
              sx={{
                px: 2,
                borderRadius: "8px",
                backgroundColor: `${error[600]} !important`,
                color: "white",
                "&:hover": {
                  bgcolor: `${error[700]} !important`,
                },
              }}
            >
              {confirmButtonText}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
